import { Title } from '@solidjs/meta'
import { createSignal, For } from 'solid-js'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Gallery from '~/components/Gallery'
import Icon from '~/components/Icon'
import ImageViewer from '~/components/ImageViewer'
import type { ImageItemType } from '~/types/ImageViewer'

const demoImages: ImageItemType[] = [
  { src: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=1600&auto=format&fit=crop', caption: 'Montañas al amanecer' },
  { src: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?q=80&w=1600&auto=format&fit=crop', caption: 'Valle verde' },
  { src: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?q=80&w=1600&auto=format&fit=crop', caption: 'Bosque neblinoso' },
  { src: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?q=80&w=1600&auto=format&fit=crop', caption: 'Sendero entre árboles' },
  { src: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?q=80&w=1600&auto=format&fit=crop', caption: 'Olas en la orilla' },
  { src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1600&auto=format&fit=crop', caption: 'Playa de arena blanca' }
]

const GalleryPage = () => {
  const [viewerOpen, setViewerOpen] = createSignal(false)
  const [viewerIndex, setViewerIndex] = createSignal(0)

  const openViewer = (i: number) => {
    setViewerIndex(i)
    setViewerOpen(true)
  }

  return (
    <>
      <Title>Gallery Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Gallery</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Cuadrícula responsive de miniaturas de imágenes con efecto hover. Combínalo con <span class="font-mono">ImageViewer</span> para abrir un visor en pantalla completa al hacer click.
          </p>
        </div>

        <div class="space-y-12">
          <Card>
            <Card.Header
              title="Galería básica"
              subtitle="Haz click en cualquier imagen para abrir el visor en pantalla completa."
              icon={<Icon icon="material-symbols-light:photo-library-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <Gallery images={demoImages} onClick={openViewer} />

              <ImageViewer images={demoImages} open={viewerOpen()} initialIndex={viewerIndex()} showThumbnails onClose={() => setViewerOpen(false)} onIndexChange={setViewerIndex} />
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades del componente Gallery." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <For each={apiRows}>
                      {row => (
                        <tr>
                          <td class="px-6 py-4 font-mono text-primary font-bold">{row.prop}</td>
                          <td class="px-6 py-4 text-foreground-subtle">{row.type}</td>
                          <td class="px-6 py-4 font-mono">{row.def}</td>
                          <td class="px-6 py-4 text-foreground-muted">{row.desc}</td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

const apiRows = [
  { prop: 'images', type: 'ImageItemType[]', def: '-', desc: 'Array de { src, caption?, alt? }.' },
  { prop: 'class', type: 'string', def: '-', desc: 'Clases personalizadas para el contenedor.' },
  { prop: 'onClick', type: '(index: number) => void', def: '-', desc: 'Callback al hacer click en una imagen.' }
]

export default GalleryPage
